import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { IconHome, IconSize } from '@amzn/stencil-react-components/icons';
import { Col, View } from '@amzn/stencil-react-components/layout';
import { Link } from '@amzn/stencil-react-components/link';
import { Text } from '@amzn/stencil-react-components/text';
import React from 'react';
import { ErrorDetailsPanel } from 'src/components/ErrorDetailsPanel/ErrorDetailsPanel';
import { FAILURE_ERROR_IMAGE_TEXT } from 'src/constants/DisplayConstants';
import {
  ErrorDisplay,
  getErrorDisplay,
  PXT_CMS_ONCALL_TICKET_URL
} from 'src/constants/ErrorMessages';
import { usePageTitle } from 'src/hooks/usePageAsTitle';
import { FailureContext } from 'src/utils/ErrorRedirectUtils';
import { getStageExternalURLs } from 'src/utils/StageConfigUtils';
import { StringUtils } from 'src/utils/StringUtils';
import './FailureErrorPage.scss';

const DEFAULT_ERROR_BUTTON_TEXT = 'Go to Admin Console Home';
const DEFAULT_ERROR_BUTTON_LINK = '/';

interface FailureErrorViewProps {
  title?: string;
  messages?: ReadonlyArray<string>;
  buttonText?: string;
  /** HTTP status or route error code, used to pick the user-facing message. */
  errorCode?: string;
  /** Engineer-facing failure context rendered in the collapsible panel. */
  failureContext?: FailureContext;
  buttonOnClick?: () => void;
}

export const FailureErrorView = (props: FailureErrorViewProps) => {
  const defaultErrorButtonOnClick = () => {
    window.location.assign(DEFAULT_ERROR_BUTTON_LINK);
  };

  // Prefer explicit title/messages when a caller supplies them; otherwise
  // derive user-facing copy from the error class so different failures read
  // distinctly instead of all showing the generic message.
  const derived: ErrorDisplay = getErrorDisplay(
    props.failureContext?.exception?.errorStatus ??
      props.failureContext?.exception?.transportCode ??
      props.errorCode
  );

  const {
    title = derived.title,
    messages = derived.messages,
    buttonText = DEFAULT_ERROR_BUTTON_TEXT,
    buttonOnClick = defaultErrorButtonOnClick,
    errorCode,
    failureContext
  } = props;

  usePageTitle(title);

  return (
    <Col
      className={'failure-error-page'}
      alignItems={'center'}
      justifyContent={'flex-start'}
    >
      <View className={'failure-error-content'}>
        <Col alignItems={'center'} gridGap={16}>
          <img
            className="failure-error-image"
            src={getStageExternalURLs().INTERNAL_SERVER_ERROR_IMAGE}
            alt={FAILURE_ERROR_IMAGE_TEXT}
            onError={(e) => {
              // The illustration is decorative; on an error page (especially a
              // connectivity failure) it may itself fail to load. Hide it
              // rather than show a broken-image icon.
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <Text fontSize="T500" fontWeight="bold" textAlign="center">
            {title}
          </Text>
          <Col gridGap={4} alignItems={'center'}>
            {messages.map((message) => (
              <Text
                key={StringUtils.generateStringHash(message)}
                fontSize="T300"
                fontWeight="regular"
                textAlign="center"
                color={'neutral70'}
              >
                {message}
              </Text>
            ))}
          </Col>
          {derived.showOncallLink && (
            <Text
              fontSize="T300"
              fontWeight="regular"
              textAlign="center"
              color={'neutral70'}
            >
              If the issue persists, please{' '}
              <Link
                href={PXT_CMS_ONCALL_TICKET_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                create a ticket using this link
              </Link>{' '}
              — attach a screenshot and copy the error details from below.
            </Text>
          )}
          <Button
            variant={ButtonVariant.Primary}
            onClick={buttonOnClick}
            icon={<IconHome aria-hidden={true} size={IconSize.Medium} />}
          >
            {buttonText}
          </Button>
          <ErrorDetailsPanel
            errorCode={errorCode}
            exception={failureContext?.exception}
            context={failureContext}
          />
        </Col>
      </View>
    </Col>
  );
};
